import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import debug from "@plannel/utils/debug-util";

const STAGE = "stage";
const PLANT = "plant";

/**
 * 스테이지(Stage) 조회/선택 전용 팝업 — old `StagePopup.jsx`와 동일하게 선택 전용(관리 화면 없음).
 * 백엔드 `stages-tree`(법인>사이트>공장>스테이지 트리)를 `organization` 컬럼 기준 AG Grid
 * treeData로 그대로 표시(old TreeGrid의 접기/펼치기 구조 재현, 기본 전체 펼침).
 * 더블클릭/[선택]은 `organization === "stage"`인 리프 행에서만 동작 — old의
 * `if (clickedRow['organization'] === 'stage')` 제약과 동일.
 *
 * props: open, onClose(), planScope, onSelect({stageCd, stageNm, plantCd, plantNm})
 */
const buildStageTreeRows = (tree, planScope) => {
  const rows = [];
  const walk = (nodes, parentPath, plant) => {
    (nodes || []).forEach(node => {
      const orgPath = [...parentPath, node.code];
      const nextPlant = node.organization === PLANT ? { plantCd: node.code, plantNm: node.name } : plant;

      rows.push({
        planScope,
        organization: node.organization,
        code: node.code,
        name: node.name,
        description: node.description,
        orgPath,
        ...(node.organization === STAGE ? { plantCd: plant?.plantCd ?? "", plantNm: plant?.plantNm ?? "" } : {})
      });

      walk(node.children, orgPath, nextPlant);
    });
  };
  walk(tree, [], null);
  return rows;
};

const StagePopup = ({ t, open, onClose, planScope, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_STAGE_SELECT"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: t("FP_NAME"), field: "name", minWidth: 160, flex: 2, editable: false },
    { headerName: t("FP_DESC_TXT"), field: "description", minWidth: 200, flex: 2, editable: false }
  ]), [t]);

  // 트리 컬럼(법인>사이트>공장>스테이지 계층 표시) — old TreeGrid의 code 컬럼(트리 표시용) 대응.
  // editable:false 명시 — FpGridSetting의 defaultColDef.editable(true)이 그대로 상속돼 더블클릭 시
  // 이름 편집이 열리는 걸 막는다(선택 전용 팝업이라 그리드 전체 읽기전용).
  const autoGroupColumnDef = useMemo(() => ({
    headerName: t("FP_ORGANIZATION"),
    minWidth: 220,
    editable: false
  }), [t]);

  const loadStages = () => {
    if (!planScope) return;
    fpCommonService.getStagesTree({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : buildStageTreeRows(response.data, planScope)))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  // stage 리프 노드에서만 선택 확정 — old의 organization==='stage' 제약과 동일. old NOBUTTONS와
  // 동일하게 더블클릭으로만 확정, 검색창/취소·확정 버튼 없음(RouteGroupPopup 선택 모드와 동일 구성).
  const onRowDoubleClicked = (e) => {
    if (e.data?.organization !== STAGE) return;
    onSelect({ stageCd: e.data.code, stageNm: e.data.name, plantCd: e.data.plantCd, plantNm: e.data.plantNm });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("FP_STAGE_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            treeData
            getDataPath={(data) => data.orgPath}
            groupDefaultExpanded={-1}
            autoGroupColumnDef={autoGroupColumnDef}
            rowSelection={{
              mode: "singleRow",
              checkboxes: false,
              enableClickSelection: true,
              isRowSelectable: (node) => node.data?.organization === STAGE
            }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(StagePopup);
